import { Ionicons } from "@expo/vector-icons";
import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { Link, router, useFocusEffect } from "expo-router";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ScrollView, View } from "react-native";
import Toast from "react-native-toast-message";
import { useDispatch } from "react-redux";

import Title from "@/components/Title";
import Button from "@/components/button/Button";
import Input from "@/components/input/Input";
import InputChoose from "@/components/input/InputChoose";
import InputDropList from "@/components/input/InputDropList";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { sendRequest } from "@/utils/ApiUtil";
import { ProfileRequest } from "@/utils/dto/accounts.request";
import {
  ApplicationStateApplicationType,
  CounterPartyProfileFIATUSRelationshipToCustomer,
} from "@/utils/dto/extracted_enums";

type FormData = {
  profileType: string;
  relationshipToCustomer: string;
  name: string;
  countryCode: string;
  state: string;
  city: string;
  postalCode: string;
  postalCodeUs: string;
  addressLine1: string;
  address: string;
};

interface CountryData {
  country: string;
  translation: string;
  code: string;
  svg: string;
  idd: string;
}

interface StateData {
  state: string;
  stateCode: string;
  countryCode: string;
}

interface CityData {
  city: string;
  stateCode: string;
  countryCode: string;
}

export default function Profile() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const globalValues = useAppSelector((state: any) => state);
  const [currentTransactionId, setCurrentTransactionId] = useState("");
  const [screenFocused, setScreenFocused] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [stateSearchTerm, setStateSearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [countryAcronym, setCountryAcronym] = useState("");
  const [countries, setCountries] = useState<
    { value: string; code: string; uniqueKey: string }[]
  >([]);
  const [states, setStates] = useState<
    { value: string; code: string; uniqueKey: string }[]
  >([]);
  const [cities, setCities] = useState<
    { value: string; code: string; uniqueKey: string }[]
  >([]);
  const [stateAcronym, setStateAcronym] = useState("");

  const resolver = classValidatorResolver(ProfileRequest, {
    validator: { skipMissingProperties: true },
  });

  const { control, handleSubmit, reset, setValue, setFocus, setError } =
    useForm<FormData>({
      mode: "onSubmit",
      resolver,
    });

  useFocusEffect(
    React.useCallback(() => {
      setScreenFocused(true);
      if (
        currentTransactionId !== globalValues.currentTransactionId ||
        globalValues.currentTransactionId === ""
      ) {
        reset();
        setCountryAcronym("");
        setCurrentTransactionId(globalValues.currentTransactionId);
      }
      return () => {
        setScreenFocused(false);
      };
    }, [globalValues.currentTransactionId, screenFocused])
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await sendRequest("get", "/countries", {
          searchTerm,
        });
        const mappedCountries = response.map((country: CountryData) => ({
          value: country.country,
          code: country.code,
          uniqueKey: `country-${country.code}`,
        }));
        setCountries(mappedCountries);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    if (searchTerm) {
      fetchData();
    }
  }, [searchTerm]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await sendRequest("get", "/states", {
          countryCode: countryAcronym,
          searchTerm: stateSearchTerm,
        });
        const mappedStates = response.map((state: StateData) => ({
          value: state.state,
          code: state.stateCode,
          uniqueKey: `state-${state.stateCode}-${state.state}`,
        }));
        setStates(mappedStates);
      } catch (error) {
        console.error("Error fetching states:", error);
      }
    };
    if (stateSearchTerm) {
      fetchData();
    }
  }, [stateSearchTerm]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await sendRequest("get", "/cities", {
          countryCode: countryAcronym,
          stateCode: stateAcronym,
          searchTerm: citySearchTerm,
        });
        const mappedStates = response.map((city: CityData) => ({
          value: city.city,
          code: city.stateCode,
          uniqueKey: `city-${city.city}-${city.stateCode}`,
        }));
        setCities(mappedStates);
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };
    if (citySearchTerm) {
      fetchData();
    }
  }, [citySearchTerm]);

  const onSubmit = (data: FormData) => {
    if (
      data.profileType === "INDIVIDUAL" &&
      !data.name.match(/^\S+(\s+\S+)+$/)
    ) {
      setError(
        "name",
        {
          type: "custom",
          message: t("validation:FULL_NAME_REQUIRED"),
        },
        { shouldFocus: true }
      );
      return;
    }

    if (data.countryCode === "US" && data.state.length > 2) {
      Toast.show({
        type: "error",
        autoHide: false,
        props: {
          text1: t("common:toast.error"),
          text2: t("common:toast.subtitle.statesus"),
        },
      });
      return;
    }
    const profileData = {
      profileType: data.profileType,
      name: data.name,
      relationshipToCustomer: data.relationshipToCustomer,
    };
    const profileAddressData = {
      countryCode: countryAcronym,
      state: data.state,
      city: data.city,
      postalCode: data.postalCode || data.postalCodeUs,
      addressLine1: data.addressLine1,
    };

    dispatch(setForm({ profileInformationData: profileData }));
    dispatch(setForm({ profileAddressData }));
    router.push("home/transaction/account");
  };

  const handleCountry = (data: { value: string; code: string }) => {
    dispatch(setForm({ country: data.value }));
    setCountryAcronym(data.code);
    setValue("countryCode", data.code, { shouldValidate: true });
    setFocus("state");
    setCountries([]);
  };

  const handleState = (data: { value: string; code: string }) => {
    setStateAcronym(data.code);
    if (countryAcronym === "US") {
      setValue("state", data.code, { shouldValidate: true });
    } else {
      setValue("state", data.value, { shouldValidate: true });
    }
    setFocus("city");
    setStates([]);
  };

  const handleCity = (data: { value: string; code: string }) => {
    setValue("city", data.value, { shouldValidate: true });
    setFocus("addressLine1");
    setCities([]);
  };

  return (
    <ScrollView contentContainerClassName="flex-grow items-center bg-white1 p-4 lg:p-0">
      <Link href="/home/transaction/register" asChild>
        <View className="absolute top-0 left-0 m-1.5 z-50">
          <Ionicons name="chevron-back-outline" size={28} color="#BDBDBD" />
        </View>
      </Link>
      <Link href="/home" asChild>
        <View className="absolute top-0 right-0 m-1 z-50">
          <Ionicons name="close-outline" size={32} color="#BDBDBD" />
        </View>
      </Link>
      <View className="my-2">
        <Title text={t("home/profile:titleinformation")} />
      </View>
      <View className="z-50 flex-grow flex-col w-full items-center justify-center">
        <View className="flex-col lg:flex-row">
          <View className="z-50 flex-col mx-4">
            <Input
              label={t("home/profile:input.name")}
              control={control}
              name="name"
            />
            <View className="z-50">
              <InputChoose
                label={t("home/profile:input.profiletype")}
                control={control}
                name="profileType"
                data={{ ApplicationStateApplicationType }}
              />
            </View>
            <View className="z-40">
              <InputChoose
                label={t("home/profile:input.relationshiptocustomer")}
                control={control}
                name="relationshipToCustomer"
                data={{ CounterPartyProfileFIATUSRelationshipToCustomer }}
              />
            </View>
            <View className="z-30">
              <InputDropList
                label={t("home/profile:input.country")}
                handleChange={(data) => {
                  handleCountry(data);
                }}
                clearValueFunction={() => {
                  setCountries([]);
                }}
                control={control}
                name="countryCode"
                data={countries}
                onSearchChange={(term) => setSearchTerm(term)}
                placeholder={t("common:placeholder.country")}
              />
            </View>
          </View>
          <View className="flex-col mx-4">
            <View className="z-30">
              <InputDropList
                label={t("home/profile:input.state")}
                handleChange={(data) => {
                  handleState(data);
                }}
                clearValueFunction={() => {
                  setStates([]);
                }}
                control={control}
                name="state"
                data={states}
                onSearchChange={(term) => setStateSearchTerm(term)}
                placeholder={t("common:placeholder.state")}
              />
            </View>
            <View className="z-20">
              <InputDropList
                label={t("home/profile:input.city")}
                handleChange={(data) => {
                  handleCity(data);
                }}
                clearValueFunction={() => {
                  setCities([]);
                }}
                control={control}
                name="city"
                data={cities}
                onSearchChange={(term) => setCitySearchTerm(term)}
                placeholder={t("common:placeholder.city")}
              />
            </View>
            <Input
              label={t("home/profile:input.addressline1")}
              control={control}
              name="addressLine1"
              type="alpha"
            />
            <Input
              label={t("home/profile:input.postalcode")}
              control={control}
              name={
                countryAcronym && countryAcronym === "US"
                  ? "postalCodeUs"
                  : "postalCode"
              }
              type="alpha"
            />
          </View>
        </View>
      </View>
      <Button
        label={t("common:button.nextstep")}
        onClick={handleSubmit(onSubmit)}
      />
    </ScrollView>
  );
}
